import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faEdit, 
  faTrashAlt, 
  faEye, 
  faPlus,
  faUserCog,
  faUserShield
} from '@fortawesome/free-solid-svg-icons';

function Administrators() {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  
  useEffect(() => {
    fetchAdministrators();
  }, [currentPage]);
  
  const fetchAdministrators = async () => {
    setIsLoading(true);
    try {
      // In a production app, this would be an actual API call
      // Simulate API call with timeout
      setTimeout(() => {
        const dummyAdmins = [
          {
            id: 1,
            username: 'admin',
            firstName: 'Admin',
            lastName: 'User',
            email: 'admin@bangalorevaluers.org',
            role: 'super',
            lastLogin: '2024-02-25T08:30:00',
            createdAt: '2023-05-15T10:00:00'
          },
          {
            id: 2,
            username: 'john.doe',
            firstName: 'John',
            lastName: 'Doe',
            email: 'john@bangalorevaluers.org',
            role: 'regular',
            lastLogin: '2024-02-20T14:15:00',
            createdAt: '2023-08-20T09:30:00'
          },
          {
            id: 3,
            username: 'sarah.smith',
            firstName: 'Sarah',
            lastName: 'Smith',
            email: 'sarah@bangalorevaluers.org',
            role: 'regular',
            lastLogin: '2024-02-24T11:20:00',
            createdAt: '2023-09-10T11:45:00'
          }
        ];
        
        setAdmins(dummyAdmins);
        setTotalPages(1);
        setIsLoading(false);
      }, 800);
    } catch (error) {
      console.error('Error fetching administrators:', error);
      setIsLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchAdministrators();
  };
  
  const handleDelete = (admin) => {
    setAdminToDelete(admin);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    try {
      // In a real app, you would make an API call here
      console.log('Deleting administrator:', adminToDelete.id);
      
      // Remove admin from state
      setAdmins(admins.filter(admin => admin.id !== adminToDelete.id));
      setShowDeleteModal(false);
      setAdminToDelete(null);
    } catch (error) {
      console.error('Error deleting administrator:', error);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setAdminToDelete(null);
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const formatDateTime = (dateString) => {
    return `${formatDate(dateString)} at ${formatTime(dateString)}`;
  };
  
  const getRoleBadge = (role) => {
    if (role === 'super') {
      return (
        <span style={{
          backgroundColor: '#002147',
          color: 'white',
          padding: '0.25rem 0.5rem',
          borderRadius: '4px',
          fontSize: '0.875rem',
          fontWeight: '500'
        }}>
          <FontAwesomeIcon icon={faUserShield} style={{ marginRight: '0.25rem' }} />
          Super Admin
        </span>
      );
    } else {
      return (
        <span style={{
          backgroundColor: '#6c757d',
          color: 'white',
          padding: '0.25rem 0.5rem',
          borderRadius: '4px',
          fontSize: '0.875rem',
          fontWeight: '500'
        }}>
          <FontAwesomeIcon icon={faUserCog} style={{ marginRight: '0.25rem' }} />
          Administrator
        </span>
      );
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Administrators</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Administrators</span>
          </li>
        </ul>
      </div>
      
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-body" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'relative', marginRight: '1rem' }}>
                <input 
                  type="text" 
                  placeholder="Search administrators..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    padding: '0.5rem 0.5rem 0.5rem 2rem', 
                    borderRadius: '4px', 
                    border: '1px solid var(--admin-border)',
                    minWidth: '250px'
                  }}
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    position: 'absolute', 
                    left: '0.5rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }}
                />
              </div>
              <button 
                type="submit"
                className="admin-btn admin-btn-primary"
                style={{ padding: '0.5rem 1rem', width: 'auto' }}
              >
                Search
              </button>
            </form>
            
            <Link to="/admin/administrators/add" className="admin-btn admin-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', width: 'auto' }}>
              <FontAwesomeIcon icon={faPlus} />
              Add Administrator
            </Link>
          </div>
        </div>
      </div>
      
      <div className="admin-table-container">
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.length > 0 ? (
                admins.map((admin) => (
                  <tr key={admin.id}>
                    <td>{admin.id}</td>
                    <td>{admin.firstName} {admin.lastName}</td>
                    <td>{admin.username}</td>
                    <td>{admin.email}</td>
                    <td>{getRoleBadge(admin.role)}</td>
                    <td>{formatDateTime(admin.lastLogin)}</td>
                    <td>
                      <div className="admin-table-actions">
                        <Link 
                          to={`/admin/administrators/${admin.id}`} 
                          className="admin-table-btn admin-table-btn-view"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Link>
                        <Link 
                          to={`/admin/administrators/${admin.id}/edit`} 
                          className="admin-table-btn admin-table-btn-edit"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                        {admin.role !== 'super' && (
                          <button 
                            className="admin-table-btn admin-table-btn-delete"
                            onClick={() => handleDelete(admin)}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                    No administrators found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            padding: '1rem', 
            borderTop: '1px solid var(--admin-border)'
          }}>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button 
                className="admin-btn"
                style={{ 
                  backgroundColor: '#f8f9fa', 
                  color: 'var(--admin-dark)',
                  padding: '0.5rem 0.75rem',
                  width: 'auto'
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(page => Math.max(page - 1, 1))}
              >
                &laquo;
              </button>
              
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className="admin-btn"
                  style={{ 
                    backgroundColor: currentPage === i + 1 ? 'var(--admin-primary)' : '#f8f9fa',
                    color: currentPage === i + 1 ? 'white' : 'var(--admin-dark)',
                    padding: '0.5rem 0.75rem',
                    width: 'auto'
                  }}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              
              <button 
                className="admin-btn"
                style={{ 
                  backgroundColor: '#f8f9fa',
                  color: 'var(--admin-dark)',
                  padding: '0.5rem 0.75rem',
                  width: 'auto'
                }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(page => Math.min(page + 1, totalPages))}
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>
      
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '400px',
            boxShadow: 'var(--admin-shadow)'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>Confirm Deletion</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              Are you sure you want to delete administrator {adminToDelete?.firstName} {adminToDelete?.lastName}? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button 
                className="admin-btn admin-btn-light"
                onClick={cancelDelete}
                style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6', width: 'auto' }}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-danger"
                onClick={confirmDelete}
                style={{ backgroundColor: 'var(--admin-danger)', width: 'auto' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default Administrators;
